import { ReactNode } from 'react';

import compact from 'lodash/compact';
import get from 'lodash/get';

import { clearQueryParamSilently } from '@cast/utils';

import { SearchBoxCriterionGroup } from './constants';
import {
  ClientSideSearchCriterion,
  Criterion,
  FreeTextPredicate,
  SearchBoxCriterion,
  SearchState,
  CriterionStorage,
} from './types';

export const getInitialValue = (criteria: Criterion[]): Record<string, any> => {
  const initial: Record<string, any> = {};
  for (const { key, initialValue } of criteria) {
    // Need strict check to allow display values with 'false' values
    if (initialValue !== undefined && initialValue !== null) {
      initial[key] = initialValue;
    }
  }
  return initial;
};

export const isClient = <T>(
  criterion: Criterion
): criterion is ClientSideSearchCriterion<T> => {
  const { prop, getValue, matcher } = criterion as ClientSideSearchCriterion;
  return prop !== undefined || getValue !== undefined || matcher !== undefined;
};

export const isServer = (criterion: Criterion): boolean => {
  return !isClient(criterion);
};

export const makeCriteria = <T>(
  ...criteria: Array<ClientSideSearchCriterion<T> | Criterion | false>
): Array<ClientSideSearchCriterion<T> | Criterion> => {
  return compact(criteria);
};

export const makeSearchBoxCriterion = <T>(
  criterion: SearchBoxCriterion<T>
): SearchBoxCriterion<T> => ({ ...criterion, group: SearchBoxCriterionGroup });

export const makeSearchBoxCriteria = <T>(
  ..._criteria: Array<SearchBoxCriterion<T> | false>
): SearchBoxCriterion<T>[] => {
  return makeCriteria(..._criteria).map(makeSearchBoxCriterion);
};

type MakeSearchQuery = {
  id: string;
  values?: Record<string, any>;
  freeText?: string;
  searchParams?: URLSearchParams;
};

export const makeSearchQuery = ({
  id,
  values,
  freeText,
  searchParams = new URLSearchParams(),
}: MakeSearchQuery): URLSearchParams => {
  if (Object.keys(values || {}).length) {
    searchParams.append(id, encodeURIComponent(JSON.stringify(values)));
  }
  if (freeText) {
    searchParams.append(`${id}_freeText`, encodeURIComponent(freeText));
  }
  return searchParams;
};

export const clearUrlState = (urlKey: string): void => {
  clearQueryParamSilently(urlKey);
  clearQueryParamSilently(`${urlKey}_sortBy`);
  clearQueryParamSilently(`${urlKey}_freeText`);
};

type MakeFreeTextPredicateArgs<T extends object> = {
  caseInsensitive?: boolean;
  searchFn?: 'includes' | 'startsWith';
  props?: Array<keyof T | string>;
};

export const makeFreeTextPredicate = <T extends object>({
  caseInsensitive,
  searchFn = 'includes',
  props,
}: MakeFreeTextPredicateArgs<T> = {}): FreeTextPredicate<T> => {
  const normalize = (val: unknown): string | undefined =>
    val == null ? undefined : String(val);

  const normalizeCase = (val: string): string =>
    caseInsensitive ? val.toLowerCase() : val;

  const makeGetter =
    (prop: string | number | symbol) =>
    (entity: T): string =>
      normalizeCase(normalize(get(entity, prop)) ?? '');

  const getAllProps = (entity: T): string[] =>
    Object.keys(entity).map((prop) =>
      normalizeCase(normalize(get(entity, prop)) ?? '')
    );

  return (entity, freeText) => {
    const text = normalizeCase(String(freeText));

    const valuesToSearch = props?.length
      ? props.map((prop) => makeGetter(prop)(entity))
      : getAllProps(entity);

    return valuesToSearch.some((value) => value?.[searchFn](text));
  };
};

export const getNoDataState = <T>(
  ctx: SearchState<T>,
  {
    components,
    hasFailed: _hasFailed,
  }: {
    components?: Partial<Record<'noResults' | 'noData' | 'failed', ReactNode>>;
    hasFailed?: boolean;
  }
): {
  hasNoResults: boolean;
  hasNoData: boolean;
  hasFailed: boolean;
  component: ReactNode;
} => {
  const state = {
    hasNoResults: false,
    hasNoData: false,
    hasFailed: false,
    component: null as ReactNode,
  };

  if (ctx.isLoading) {
    return state;
  }

  const hasFailed = !!_hasFailed || !!ctx.error;
  if (hasFailed) {
    state.hasFailed = true;
    if (components?.failed) {
      state.component = components.failed;
    }
    return state;
  }

  const noResultsFound =
    (!!ctx.data?.length && !ctx.filteredData?.length) ||
    !!ctx.noResultWithAppliedFilters;
  if (noResultsFound) {
    state.hasNoResults = true;
    if (components?.noResults) {
      state.component = components.noResults;
    }
    return state;
  }

  // For server-side sort: only treat empty data as "no data" when sortingState
  // is non-null (i.e. a sort was actually requested). Null means no sort yet.
  const noData =
    !ctx.data?.length && (!ctx.serverSideSort || ctx.sortingState !== null);
  if (noData) {
    state.hasNoData = true;
    if (components?.noData) {
      state.component = components.noData;
    }
    return state;
  }

  return state;
};

type StorageOverrides<STORAGE extends CriterionStorage = CriterionStorage> = {
  [KEY in keyof STORAGE]: STORAGE[KEY] extends (
    ...args: infer ARGS
  ) => infer RETURN
    ? (...args: [...ARGS, orig: STORAGE[KEY]]) => RETURN
    : never;
};

const proxyStorage = (
  storage: CriterionStorage,
  overrides?: Partial<StorageOverrides>
): CriterionStorage => {
  return {
    getItem: (key): string | null => {
      if (overrides?.getItem) {
        return overrides.getItem(key, storage.getItem.bind(storage));
      }
      return storage.getItem(key);
    },

    setItem: (key, value): void => {
      if (overrides?.setItem) {
        overrides.setItem(key, value, storage.setItem.bind(storage));
        return;
      }
      storage.setItem(key, value);
    },

    removeItem: (key): void => {
      if (overrides?.removeItem) {
        overrides.removeItem(key, storage.removeItem.bind(storage));
        return;
      }
      storage.removeItem(key);
    },
  };
};

type MapStorage = (storage: CriterionStorage) => CriterionStorage;
type MapStorageOver<VALUE> = (val: VALUE) => MapStorage;

export const overrideKey: MapStorageOver<string> = (newKey) => (storage) =>
  proxyStorage(storage, {
    getItem: (_, orig) => orig(newKey),
    setItem: (_, value, orig) => orig(newKey, value),
    removeItem: (_, orig) => orig(newKey),
  });

export const makeStorage = (
  storage: CriterionStorage,
  ...[firstMapper, ...rest]: MapStorage[]
): CriterionStorage => {
  if (!firstMapper) {
    return storage;
  }
  const mappedStorage = firstMapper(storage);
  return makeStorage(mappedStorage, ...rest);
};
